<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { getUndefinedVariablesInMessage } from '@chatwoot/utils';
import { getExtendedMessageVariables } from 'shared/helpers/messageVariablesHelper';
import { useMapGetter } from 'dashboard/composables/store';
import { useOperatingProceduresStore } from 'dashboard/stores/operatingProcedures';

import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import AutomationPreviewAccordion from './AutomationPreviewAccordion.vue';
import { BRANCHING_NODE_TYPES, NODE_TYPES } from '../helpers/sopFlow';

const props = defineProps({
  node: { type: Object, required: true },
  showBranches: { type: Boolean, default: true },
  // Simulator passes its per-node run state; without it (Node Settings
  // preview) a local throwaway state keeps the checklist/feedback interactive.
  runData: { type: Object, default: null },
  // The live simulator renders feedback itself, after its branch/next controls.
  showFeedback: { type: Boolean, default: true },
  // Operating mode (agent-facing walkthrough): confirm delegates to the
  // parent, which executes the automation for real and marks the step done.
  operating: { type: Boolean, default: false },
  confirming: { type: Boolean, default: false },
  // Resolvable (automatic) variables for the current context. The walkthrough
  // passes the live conversation's map; the builder preview falls back to the
  // context-free map, which has the same automatic variable names.
  messageVariables: {
    type: Object,
    default: () => getExtendedMessageVariables({ conversation: { meta: {} } }),
  },
});

const emit = defineEmits(['confirm']);

const { t } = useI18n();

const localRunData = ref({});

watch(
  () => props.node.id,
  nodeId => {
    if (!localRunData.value[nodeId]) {
      localRunData.value[nodeId] = {
        checked: {},
        feedback: '',
        confirmed: false,
      };
    }
  },
  { immediate: true }
);

const run = computed(() => props.runData || localRunData.value[props.node.id]);

// Older persisted runs predate the variables field.
watch(
  run,
  runValue => {
    if (runValue && !runValue.variables) runValue.variables = {};
  },
  { immediate: true }
);

const nodeData = computed(() => props.node.data || {});

const sopStore = useOperatingProceduresStore();
const referencedSop = computed(() =>
  sopStore.getById(nodeData.value.operating_procedure_id)
);

// Custom {{variables}} in the canned response the agent must fill before
// confirming; values live in runData so they persist with the run.
const cannedResponses = useMapGetter('getCannedResponses');
const customVariables = computed(() => {
  const automation = nodeData.value.automation;
  if (automation?.type !== 'canned_response') return [];
  const content = cannedResponses.value.find(
    item => item.id === Number(automation.id)
  )?.content;
  if (!content) return [];
  return getUndefinedVariablesInMessage({
    message: content,
    variables: props.messageVariables,
  });
});

const hasUnfilledVariables = computed(() =>
  customVariables.value.some(name => !run.value?.variables?.[name]?.trim())
);

// Keep the original index: run.checked is keyed by position in the saved items.
const items = computed(() =>
  (nodeData.value.items || [])
    .map((text, index) => ({ text, index }))
    .filter(item => item.text)
);
const isBranching = computed(() =>
  BRANCHING_NODE_TYPES.includes(props.node.type)
);

const automationTypeLabel = computed(() => {
  const type = nodeData.value.automation?.type;
  return type ? t(`SOPS.AUTOMATION_TYPES.${type.toUpperCase()}`) : '';
});

const automationMode = computed(() => nodeData.value.mode || 'manual');

const onConfirm = () => {
  if (props.operating) {
    emit('confirm', props.node);
    return;
  }
  run.value.confirmed = true;
};
</script>

<template>
  <div class="flex min-w-0 flex-col gap-3">
    <div class="rounded-lg bg-n-alpha-black2 px-3 py-2">
      <p class="mb-0 text-xs uppercase tracking-wide text-n-slate-10">
        {{ t(`SOPS.NODES.${node.type.toUpperCase()}`) }}
      </p>
      <p
        v-if="nodeData.label"
        class="mb-0 whitespace-pre-wrap break-words text-sm text-n-slate-12"
      >
        {{ nodeData.label }}
      </p>
    </div>

    <ul v-if="items.length" class="mb-0 flex list-none flex-col gap-1.5">
      <li
        v-for="item in items"
        :key="item.index"
        class="flex items-start gap-2 text-sm text-n-slate-12"
      >
        <Checkbox v-model="run.checked[item.index]" class="mt-0.5 shrink-0" />
        <span
          class="min-w-0 cursor-pointer whitespace-pre-wrap break-words"
          :class="{ 'text-n-slate-10 line-through': run.checked[item.index] }"
          @click="run.checked[item.index] = !run.checked[item.index]"
        >
          {{ item.text }}
        </span>
      </li>
    </ul>

    <p
      v-if="node.type === NODE_TYPES.REFERENCE && referencedSop"
      class="mb-0 flex items-start gap-1.5 rounded-lg bg-n-alpha-black2 px-3 py-2 text-sm text-n-slate-12"
    >
      <span class="i-lucide-workflow mt-0.5 size-4 shrink-0" />
      <span class="min-w-0 break-words">{{ referencedSop.name }}</span>
    </p>

    <div
      v-if="nodeData.automation"
      class="rounded-lg bg-n-alpha-black2 px-3 py-2"
    >
      <span class="text-xs uppercase tracking-wide text-n-slate-10">
        {{ automationTypeLabel }}
      </span>
      <AutomationPreviewAccordion
        :id="nodeData.automation.id"
        :type="nodeData.automation.type"
        :name="nodeData.automation.name"
      />
      <p
        class="mb-0 mt-1.5 flex items-center gap-1.5 text-xs"
        :class="
          automationMode === 'auto_pilot' ? 'text-n-teal-11' : 'text-n-amber-11'
        "
      >
        <span
          class="size-3.5 shrink-0"
          :class="
            automationMode === 'auto_pilot'
              ? 'i-lucide-rocket'
              : 'i-lucide-hand'
          "
        />
        {{ t(`SOPS.BUILDER.PANEL.MODE_${automationMode.toUpperCase()}_HINT`) }}
      </p>
      <template
        v-if="
          automationMode === 'manual' || operating || customVariables.length
        "
      >
        <div
          v-if="customVariables.length && !run.confirmed"
          class="mt-2 flex flex-col gap-1.5"
        >
          <p class="mb-0 text-xs font-medium text-n-slate-11">
            {{ t('SOPS.SIMULATOR.VARIABLES_LABEL') }}
          </p>
          <Input
            v-for="name in customVariables"
            :key="name"
            v-model="run.variables[name]"
            :placeholder="name"
          />
        </div>
        <Button
          v-if="
            !run.confirmed &&
            (automationMode === 'manual' || customVariables.length)
          "
          :label="t('SOPS.SIMULATOR.CONFIRM_AUTOMATION')"
          icon="i-lucide-check"
          class="mt-2 w-full"
          sm
          teal
          faded
          :is-loading="confirming"
          :disabled="hasUnfilledVariables"
          @click="onConfirm"
        />
        <p
          v-else-if="run.confirmed"
          class="mb-0 mt-2 flex items-center gap-1.5 text-xs text-n-teal-11"
        >
          <span class="i-lucide-circle-check size-3.5 shrink-0" />
          {{ t('SOPS.SIMULATOR.CONFIRMED') }}
        </p>
      </template>
    </div>

    <div
      v-if="showBranches && isBranching && nodeData.branches?.length"
      class="flex min-w-0 flex-col gap-1.5"
    >
      <Button
        v-for="branch in nodeData.branches"
        :key="branch.id"
        disabled
        sm
        slate
        faded
        justify="start"
        class="!h-auto whitespace-normal py-1.5 text-left"
      >
        <span class="min-w-0 whitespace-pre-wrap break-words">
          {{ branch.label || t('SOPS.BUILDER.PANEL.BRANCH_PLACEHOLDER') }}
        </span>
      </Button>
    </div>

    <div v-if="showFeedback" class="border-t border-n-strong pt-3">
      <TextArea
        v-model="run.feedback"
        class="!h-auto shrink-0"
        :label="t('SOPS.SIMULATOR.FEEDBACK_LABEL')"
        :placeholder="t('SOPS.SIMULATOR.FEEDBACK_PLACEHOLDER')"
        auto-height
        min-height="5.25rem"
        max-height="none"
      />
    </div>
  </div>
</template>
